import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    CoffeeSubmissionStatus,
    CoffeeSubmissionState,
    CoffeeSubmissionStep,
    CoffeeSubmissionResult,
    DEFAULT_COFFEE_SUBMISSION_STEPS
} from './CoffeeStatusTypes';

interface UseCoffeeSubmissionOptions {
    // fired once auto-dismiss elapses after a successful completion
    onComplete?: () => void;
    // delay (ms) between landing on `completed` and auto-resetting to `idle`
    autoDismissMs?: number;
}

// custom hook for managing coffee submission state
export const useCoffeeSubmission = (options: UseCoffeeSubmissionOptions = {}) => {
    const { onComplete, autoDismissMs = 2000 } = options;
    const onCompleteRef = useRef(onComplete);
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);
    // main submission state
    const [submissionState, setSubmissionState] = useState<CoffeeSubmissionState>({
        isSubmitting: false,
        currentStatus: 'idle',
        statusInfo: {
            status: 'idle',
            message: 'Ready to submit coffee entry',
            progress: 0,
            retryCount: 0,
            maxRetries: 3
        },
        steps: [...DEFAULT_COFFEE_SUBMISSION_STEPS]
    });

    // refs for tracking
    const submissionStartTime = useRef<Date | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const autoDismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const clearAutoDismiss = useCallback(() => {
        if (autoDismissTimeoutRef.current) {
            clearTimeout(autoDismissTimeoutRef.current);
            autoDismissTimeoutRef.current = null;
        }
    }, []);

    // helper function to update submission state
    const updateSubmissionState = useCallback((updates: Partial<CoffeeSubmissionState>) => {
        setSubmissionState(prev => ({ ...prev, ...updates }));
    }, []);

    // helper function to update status info
    const updateStatusInfo = useCallback((updates: Partial<CoffeeSubmissionState['statusInfo']>) => {
        setSubmissionState(prev => ({
            ...prev,
            statusInfo: { ...prev.statusInfo, ...updates }
        }));
    }, []);

    // helper function to update specific step
    const updateStep = useCallback((stepId: string, updates: Partial<CoffeeSubmissionStep>) => {
        setSubmissionState(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId ? { ...step, ...updates } : step
            )
        }));
    }, []);

    // start submission process
    const startSubmission = useCallback(() => {
        submissionStartTime.current = new Date();
        updateSubmissionState({
            isSubmitting: true,
            currentStatus: 'validating',
            statusInfo: {
                status: 'validating',
                message: 'validating form data...',
                progress: 10,
                startedAt: submissionStartTime.current,
                retryCount: 0,
                maxRetries: 3
            },
            steps: DEFAULT_COFFEE_SUBMISSION_STEPS.map(step => ({
                ...step,
                status: step.id === 'validation' ? 'in_progress' : 'pending'
            }))
        });
    }, [updateSubmissionState]);

    // move to next status
    const setStatus = useCallback((
        status: CoffeeSubmissionStatus,
        message?: string,
        progress?: number
    ) => {
        updateSubmissionState({
            currentStatus: status,
            statusInfo: {
                status,
                message: message || `${status}...`,
                progress: progress || submissionState.statusInfo.progress,
                startedAt: submissionState.statusInfo.startedAt,
                retryCount: submissionState.statusInfo.retryCount,
                maxRetries: submissionState.statusInfo.maxRetries
            }
        });

        // update corresponding step
        const stepMap: Record<CoffeeSubmissionStatus, string> = {
            'validating': 'validation',
            'creating': 'creation',
            'uploading': 'image_upload',
            'processing': 'processing',
            'completed': 'completion',
            'failed': 'completion',
            'error': 'completion',
            'retrying': 'validation',
            'idle': 'validation'
        };

        const stepId = stepMap[status];
        if (stepId) {
            // mark current step as in progress
            updateStep(stepId, {
                status: status === 'completed' ? 'completed' :
                    status === 'failed' || status === 'error' ? 'failed' :
                        'in_progress',
                message: message,
                timestamp: new Date()
            });

            // mark previous steps as completed
            setSubmissionState(prev => ({
                ...prev,
                steps: prev.steps.map(step => {
                    const stepIndex = DEFAULT_COFFEE_SUBMISSION_STEPS.findIndex(s => s.id === step.id);
                    const currentStepIndex = DEFAULT_COFFEE_SUBMISSION_STEPS.findIndex(s => s.id === stepId);

                    if (stepIndex < currentStepIndex && step.status !== 'completed') {
                        return { ...step, status: 'completed' as const, timestamp: new Date() };
                    }
                    return step;
                })
            }));
        }
    }, [submissionState.statusInfo, updateSubmissionState, updateStep]);

    // handle successful completion
    const completeSubmission = useCallback((result: Partial<CoffeeSubmissionResult>) => {
        const completedAt = new Date();
        const duration = submissionStartTime.current
            ? completedAt.getTime() - submissionStartTime.current.getTime()
            : 0;

        updateSubmissionState({
            isSubmitting: false,
            currentStatus: 'completed',
            statusInfo: {
                status: 'completed',
                message: `coffee entry created successfully! (${(duration / 1000).toFixed(1)}s)`,
                progress: 100,
                startedAt: submissionState.statusInfo.startedAt,
                completedAt,
                retryCount: submissionState.statusInfo.retryCount,
                maxRetries: submissionState.statusInfo.maxRetries
            },
            result: {
                success: true,
                message: 'coffee entry created successfully!',
                ...result,
                statusInfo: submissionState.statusInfo
            }
        });

        // mark all steps as completed
        setSubmissionState(prev => ({
            ...prev,
            steps: prev.steps.map(step => ({
                ...step,
                status: 'completed' as const,
                timestamp: new Date()
            }))
        }));

        toast.success('coffee entry created successfully!');

        // schedule silent auto-dismiss: reset state to idle and notify parent so the wizard
        // can unmount and the underlying list can refetch (failed/error states stay sticky)
        clearAutoDismiss();
        autoDismissTimeoutRef.current = setTimeout(() => {
            autoDismissTimeoutRef.current = null;
            setSubmissionState({
                isSubmitting: false,
                currentStatus: 'idle',
                statusInfo: {
                    status: 'idle',
                    message: 'ready to submit coffee entry',
                    progress: 0,
                    retryCount: 0,
                    maxRetries: 3
                },
                steps: [...DEFAULT_COFFEE_SUBMISSION_STEPS]
            });
            submissionStartTime.current = null;
            try {
                onCompleteRef.current?.();
            } catch (err) {
                console.error('onComplete callback failed:', err);
            }
        }, autoDismissMs);
    }, [submissionState.statusInfo, updateSubmissionState, clearAutoDismiss, autoDismissMs]);

    // handle failure with enhanced error tracking
    const failSubmission = useCallback((
        error: string, 
        allowRetry: boolean = true,
        errorDetails?: string,
        failedFormStep?: number
    ) => {
        // a failure must never auto-dismiss — cancel any pending timer from a prior completion
        clearAutoDismiss();

        const retryCount = submissionState.statusInfo.retryCount || 0;
        const maxRetries = submissionState.statusInfo.maxRetries || 3;
        const canRetry = allowRetry && retryCount < maxRetries;

        // find the current step that was in progress
        const currentStep = submissionState.steps.find(step => step.status === 'in_progress');
        const failedStepId = currentStep?.id;

        // map step id to form step number if not provided
        const stepToFormStep: Record<string, number> = {
            'validation': 4,
            'creation': 4,
            'image_upload': 1,
            'processing': 4,
            'completion': 4
        };
        const resolvedFormStep = failedFormStep || (failedStepId ? stepToFormStep[failedStepId] : undefined);

        updateSubmissionState({
            isSubmitting: false,
            currentStatus: canRetry ? 'failed' : 'error',
            statusInfo: {
                status: canRetry ? 'failed' : 'error',
                message: canRetry ? `submission failed (${retryCount + 1}/${maxRetries + 1})` : 'submission failed',
                error,
                errorDetails,
                failedStep: failedStepId,
                failedFormStep: resolvedFormStep,
                progress: submissionState.statusInfo.progress,
                startedAt: submissionState.statusInfo.startedAt,
                retryCount: retryCount + 1,
                maxRetries
            },
            result: {
                success: false,
                error,
                statusInfo: submissionState.statusInfo
            }
        });

        // mark current step as failed
        if (currentStep) {
            updateStep(currentStep.id, {
                status: 'failed',
                message: error,
                timestamp: new Date()
            });
        }

        toast.error(`submission failed: ${error}`);
    }, [submissionState.statusInfo, submissionState.steps, updateSubmissionState, updateStep, clearAutoDismiss]);

    // retry submission
    const retrySubmission = useCallback(() => {
        clearAutoDismiss();
        const retryCount = submissionState.statusInfo.retryCount || 0;
        const maxRetries = submissionState.statusInfo.maxRetries || 3;

        if (retryCount >= maxRetries) {
            toast.error('maximum retry attempts reached');
            return false;
        }

        updateSubmissionState({
            isSubmitting: true,
            currentStatus: 'retrying',
            statusInfo: {
                ...submissionState.statusInfo,
                status: 'retrying',
                message: `retrying submission (${retryCount + 1}/${maxRetries})...`,
                progress: 5
            },
            steps: DEFAULT_COFFEE_SUBMISSION_STEPS.map(step => ({
                ...step,
                status: 'pending'
            }))
        });

        return true;
    }, [submissionState.statusInfo, updateSubmissionState, clearAutoDismiss]);

    // reset submission state
    const resetSubmission = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        clearAutoDismiss();

        setSubmissionState({
            isSubmitting: false,
            currentStatus: 'idle',
            statusInfo: {
                status: 'idle',
                message: 'ready to submit coffee entry',
                progress: 0,
                retryCount: 0,
                maxRetries: 3
            },
            steps: [...DEFAULT_COFFEE_SUBMISSION_STEPS]
        });

        submissionStartTime.current = null;
    }, [clearAutoDismiss]);

    // cleanup function
    const cleanup = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }
        clearAutoDismiss();
    }, [clearAutoDismiss]);

    return {
        submissionState,
        startSubmission,
        setStatus,
        completeSubmission,
        failSubmission,
        retrySubmission,
        resetSubmission,
        cleanup,

        // convenience getters
        isSubmitting: submissionState.isSubmitting,
        currentStatus: submissionState.currentStatus,
        statusInfo: submissionState.statusInfo,
        steps: submissionState.steps,
        result: submissionState.result,
        canRetry: submissionState.currentStatus === 'failed' &&
            (submissionState.statusInfo.retryCount || 0) < (submissionState.statusInfo.maxRetries || 3),
        // error tracking getters
        failedStep: submissionState.statusInfo.failedStep,
        failedFormStep: submissionState.statusInfo.failedFormStep,
        errorMessage: submissionState.statusInfo.error,
        errorDetails: submissionState.statusInfo.errorDetails
    };
}; 